import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  CheckCircle2,
  CircleDashed,
  Copy,
  Euro,
  FileBadge2,
  Filter,
  Mail,
  MessageCircle,
  Pencil,
  Plus,
  Printer,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";
import UIButton from "../components/UIButton";
import UIInput from "../components/UIInput";
import UILabel from "../components/UILabel";
import { notify } from "../utils/notify";
import { getCompanySettings } from "../services/companySettingsService";

const emptyItem = {
  id: "",
  description: "",
  quantity: 1,
  unit: "pcs",
  unitPrice: 0,
};

const initialForm = {
  offerNumber: "",
  title: "",
  projectId: "",
  projectName: "",
  clientId: "",
  clientName: "",
  clientEmail: "",
  clientPhone: "",
  address: "",
  status: "draft",
  issueDate: "",
  validUntil: "",
  taxRate: 19,
  notes: "",
  terms: "",
  items: [{ ...emptyItem, id: `item-${Date.now()}` }],
};

const ui = {
  sq: {
    title: "Ofertat / Kostenvoranschlag",
    subtitle: "Krijo dhe menaxho ofertat për projekte renovimi, materiale dhe punë.",
    newOffer: "Ofertë e re",
    editOffer: "Edito ofertën",
    totalOffers: "Totali ofertave",
    approvedOffers: "Të aprovuara",
    pendingValue: "Vlera draft / dërguar",
    approvedValue: "Vlera e aprovuar",
    status: "Statusi",
    allStatuses: "Të gjitha statuset",
    draft: "Draft",
    sent: "Dërguar",
    approved: "Aprovuar",
    rejected: "Refuzuar",
    noOffers: "Ende nuk ka oferta",
    noOffersText: "Krijo ofertën e parë dhe do të shfaqet këtu.",
    noResults: "Nuk u gjet asnjë ofertë",
    noResultsText: "Provo një kërkim ose filtër tjetër.",
    searchPlaceholder: "Kërko ofertë, projekt ose klient",
    listTitle: "Lista e ofertave",
    listSubtitle: "oferta sipas filtrit",
    details: "Detaje oferte",
    overview: "Përmbledhje",
    items: "Pozicionet",
    clientProject: "Klient / Projekt",
    pricing: "Çmimet",
    notesBlock: "Shënime dhe kushte",
    offerNumber: "Nr. oferta",
    titleField: "Titulli i ofertës",
    projectLink: "Lidh projekt (opsionale)",
    noProjectLinked: "Pa projekt të lidhur",
    clientLink: "Lidh klient (opsionale)",
    noClientLinked: "Pa klient të lidhur",
    projectName: "Emri i projektit",
    clientName: "Emri i klientit",
    address: "Adresa",
    issueDate: "Data e ofertës",
    validUntil: "Vlen deri më",
    taxRate: "TVSH %",
    notes: "Shënime",
    notesPlaceholder: "Përshkrim shtesë për klientin...",
    terms: "Kushtet",
    termsPlaceholder: "Afatet, pagesa, vlefshmëria e ofertës...",
    lineItems: "Pozicionet e ofertës",
    description: "Përshkrimi",
    quantity: "Sasia",
    unit: "Njësia",
    unitPrice: "Çmimi njësi",
    lineTotal: "Totali",
    addItem: "Shto pozicion",
    subtotal: "Nëntotali",
    taxAmount: "TVSH",
    grandTotal: "Totali final",
    save: "Ruaj ofertën",
    update: "Përditëso ofertën",
    cancel: "Anulo",
    createdAt: "Krijuar",
    openOffer: "Hape ofertën",
    totals: "Totale",
    confirmDelete: "A dëshiron ta fshish këtë ofertë?",
    saved: "Oferta u ruajt me sukses.",
    updated: "Oferta u përditësua me sukses.",
    deleted: "Oferta u fshi me sukses.",
    saveError: "Gabim gjatë ruajtjes së ofertës.",
    loadError: "Gabim gjatë leximit të ofertave.",
    requiredTitle: "Shkruaj titullin e ofertës.",
    requiredItem: "Shto të paktën një pozicion me përshkrim.",
    statusesReady: "Statuset janë gati për workflow real me dërgim, aprovim dhe konvertim në faturë.",
    generatedLabel: "Gjeneruar automatikisht",
    printOffer: "Printo ofertën",
    shareTools: "Dërgo ofertën",
    shareSubtitle: "Dërgo ofertën me Gmail, WhatsApp ose kopjo tekstin.",
    sendGmail: "Gmail",
    sendWhatsApp: "WhatsApp",
    copyText: "Kopjo tekstin",
    gmailMissing: "Shto email-in e klientit për ta dërguar me Gmail.",
    whatsappMissing: "Shto numrin e telefonit të klientit për ta dërguar me WhatsApp.",
    copied: "Teksti i ofertës u kopjua.",
    printTitle: "Dokument oferte për printim",
    page: "Faqe",
    contactData: "Kontaktet e klientit",
    clientEmail: "Email i klientit",
    clientPhone: "Telefoni i klientit",
    whatsappHelp: "WhatsApp e merr numrin nga klienti i lidhur te Clients → Telefon. Numri duhet të jetë në format ndërkombëtar, p.sh. +38349123456 ose +4917612345678.",
    gmailHelp: "Gmail e merr email-in nga klienti i lidhur te Clients → Email.",
    missingPhoneInline: "Ky klient nuk ka numër telefoni. Shtoje te Clients → Edit client → Telefon.",
    missingEmailInline: "Ky klient nuk ka email. Shtoje te Clients → Edit client → Email.",
    printPreparedFor: "Ofertë për",
    signature: "Nënshkrimi",
    thankYou: "Faleminderit për interesimin dhe besimin.",
  },
  de: {
    title: "Angebote / Kostenvoranschlag",
    subtitle: "Erstelle und verwalte Angebote für Renovierungsprojekte, Material und Arbeit.",
    newOffer: "Neues Angebot",
    editOffer: "Angebot bearbeiten",
    totalOffers: "Gesamtangebote",
    approvedOffers: "Genehmigt",
    pendingValue: "Wert Entwurf / Gesendet",
    approvedValue: "Genehmigter Wert",
    status: "Status",
    allStatuses: "Alle Status",
    draft: "Entwurf",
    sent: "Gesendet",
    approved: "Genehmigt",
    rejected: "Abgelehnt",
    noOffers: "Noch keine Angebote",
    noOffersText: "Erstelle das erste Angebot und es erscheint hier.",
    noResults: "Keine Angebote gefunden",
    noResultsText: "Versuche eine andere Suche oder einen anderen Filter.",
    searchPlaceholder: "Angebot, Projekt oder Kunde suchen",
    listTitle: "Angebotsliste",
    listSubtitle: "Angebote nach Filter",
    details: "Angebotsdetails",
    overview: "Übersicht",
    items: "Positionen",
    clientProject: "Kunde / Projekt",
    pricing: "Preise",
    notesBlock: "Notizen und Bedingungen",
    offerNumber: "Angebotsnr.",
    titleField: "Angebotstitel",
    projectLink: "Projekt verknüpfen (optional)",
    noProjectLinked: "Kein verknüpftes Projekt",
    clientLink: "Kunde verknüpfen (optional)",
    noClientLinked: "Kein verknüpfter Kunde",
    projectName: "Projektname",
    clientName: "Kundenname",
    address: "Adresse",
    issueDate: "Angebotsdatum",
    validUntil: "Gültig bis",
    taxRate: "MwSt %",
    notes: "Notizen",
    notesPlaceholder: "Zusätzliche Beschreibung für den Kunden...",
    terms: "Bedingungen",
    termsPlaceholder: "Fristen, Zahlung, Gültigkeit des Angebots...",
    lineItems: "Angebotspositionen",
    description: "Beschreibung",
    quantity: "Menge",
    unit: "Einheit",
    unitPrice: "Einzelpreis",
    lineTotal: "Gesamt",
    addItem: "Position hinzufügen",
    subtotal: "Zwischensumme",
    taxAmount: "MwSt",
    grandTotal: "Endsumme",
    save: "Angebot speichern",
    update: "Angebot aktualisieren",
    cancel: "Abbrechen",
    createdAt: "Erstellt",
    openOffer: "Angebot öffnen",
    totals: "Summen",
    confirmDelete: "Dieses Angebot wirklich löschen?",
    saved: "Angebot wurde gespeichert.",
    updated: "Angebot wurde aktualisiert.",
    deleted: "Angebot wurde gelöscht.",
    saveError: "Fehler beim Speichern des Angebots.",
    loadError: "Fehler beim Laden der Angebote.",
    requiredTitle: "Bitte einen Angebotstitel eingeben.",
    requiredItem: "Bitte mindestens eine Position mit Beschreibung hinzufügen.",
    statusesReady: "Die Status sind bereit für einen echten Workflow mit Versand, Freigabe und Umwandlung in eine Rechnung.",
    generatedLabel: "Automatisch erzeugt",
    printOffer: "Angebot drucken",
    shareTools: "Angebot senden",
    shareSubtitle: "Sende das Angebot per Gmail, WhatsApp oder kopiere den Text.",
    sendGmail: "Gmail",
    sendWhatsApp: "WhatsApp",
    copyText: "Text kopieren",
    gmailMissing: "Bitte Kunden-E-Mail hinzufügen, um über Gmail zu senden.",
    whatsappMissing: "Bitte Kundentelefon hinzufügen, um über WhatsApp zu senden.",
    copied: "Angebotstext wurde kopiert.",
    printTitle: "Angebotsdokument zum Drucken",
    page: "Seite",
    contactData: "Kundendaten",
    clientEmail: "Kunden-E-Mail",
    clientPhone: "Kundentelefon",
    whatsappHelp: "WhatsApp verwendet die Nummer des verknüpften Kunden aus Clients → Telefon. Die Nummer sollte im internationalen Format sein, z. B. +38349123456 oder +4917612345678.",
    gmailHelp: "Gmail verwendet die E-Mail des verknüpften Kunden aus Clients → E-Mail.",
    missingPhoneInline: "Für diesen Kunden ist keine Telefonnummer gespeichert. Bitte in Clients → Kunde bearbeiten → Telefon eintragen.",
    missingEmailInline: "Für diesen Kunden ist keine E-Mail gespeichert. Bitte in Clients → Kunde bearbeiten → E-Mail eintragen.",
    printPreparedFor: "Angebot für",
    signature: "Unterschrift",
    thankYou: "Vielen Dank für Ihr Interesse und Vertrauen.",
  },
};

const statuses = ["draft", "sent", "approved", "rejected"];
const detailTabs = ["overview", "items", "clientProject", "pricing", "notesBlock"];

function n(value) {
  return Number(value || 0);
}

function formatMoney(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(n(value));
}

function formatDate(value) {
  if (!value) return "—";
  if (typeof value?.toDate === "function") return value.toDate().toLocaleDateString("de-DE");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("de-DE");
}

function getToday() {
  return new Date().toISOString().slice(0, 10);
}

function getValidUntil() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return date.toISOString().slice(0, 10);
}

function nextOfferNumber(existing = []) {
  const year = new Date().getFullYear();
  const count = Array.isArray(existing)
    ? existing.filter((item) => String(item.offerNumber || "").includes(`ANG-${year}`)).length + 1
    : 1;
  return `ANG-${year}-${String(count).padStart(3, "0")}`;
}

function ensureItem(item, index) {
  return {
    id: item?.id || `item-${Date.now()}-${index}`,
    description: item?.description || "",
    quantity: n(item?.quantity || 1),
    unit: item?.unit || "pcs",
    unitPrice: n(item?.unitPrice || 0),
  };
}

function computeTotals(items = [], taxRate = 0) {
  const subtotal = items.reduce((sum, item) => sum + n(item.quantity) * n(item.unitPrice), 0);
  const taxAmount = subtotal * (n(taxRate) / 100);
  const total = subtotal + taxAmount;
  return { subtotal, taxAmount, total };
}

function normalizeOffer(docSnap) {
  const data = docSnap.data() || {};
  const items = Array.isArray(data.items) ? data.items.map(ensureItem) : [];
  const totals = computeTotals(items, data.taxRate);
  return {
    id: docSnap.id,
    offerNumber: data.offerNumber || "",
    title: data.title || "",
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    clientId: data.clientId || "",
    clientName: data.clientName || "",
    clientEmail: data.clientEmail || "",
    clientPhone: data.clientPhone || "",
    address: data.address || "",
    status: data.status || "draft",
    issueDate: data.issueDate || "",
    validUntil: data.validUntil || "",
    taxRate: n(data.taxRate || 19),
    notes: data.notes || "",
    terms: data.terms || "",
    items,
    subtotal: n(data.subtotal || totals.subtotal),
    taxAmount: n(data.taxAmount || totals.taxAmount),
    total: n(data.total || totals.total),
    createdAt: data.createdAt || "",
    updatedAt: data.updatedAt || "",
  };
}

function buildPayload(form) {
  const items = (form.items || [])
    .map(ensureItem)
    .filter((item) => String(item.description || "").trim());
  const totals = computeTotals(items, form.taxRate);
  return {
    offerNumber: String(form.offerNumber || "").trim(),
    title: String(form.title || "").trim(),
    projectId: form.projectId || "",
    projectName: String(form.projectName || "").trim(),
    clientId: form.clientId || "",
    clientName: String(form.clientName || "").trim(),
    clientEmail: String(form.clientEmail || "").trim(),
    clientPhone: String(form.clientPhone || "").trim(),
    address: String(form.address || "").trim(),
    status: form.status || "draft",
    issueDate: form.issueDate || "",
    validUntil: form.validUntil || "",
    taxRate: n(form.taxRate || 0),
    notes: String(form.notes || "").trim(),
    terms: String(form.terms || "").trim(),
    items,
    subtotal: totals.subtotal,
    taxAmount: totals.taxAmount,
    total: totals.total,
    updatedAt: serverTimestamp(),
  };
}


function safeText(value) {
  return String(value ?? "").replace(/[&<>"]/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
  }[char]));
}

function findLinkedClient(offer, clients = []) {
  return clients.find((item) => item.id === offer?.clientId)
    || clients.find((item) => String(item.name || item.companyName || item.contactPerson || "").trim() === String(offer?.clientName || "").trim())
    || null;
}

function getClientPhone(offer, clients = []) {
  const ownPhone = offer?.clientPhone || offer?.phone || "";
  if (String(ownPhone).trim()) return String(ownPhone).trim();
  const client = findLinkedClient(offer, clients);
  return client?.phone || client?.mobile || client?.telephone || client?.tel || "";
}

function getClientEmail(offer, clients = []) {
  const ownEmail = offer?.clientEmail || offer?.email || "";
  if (String(ownEmail).trim()) return String(ownEmail).trim();
  const client = findLinkedClient(offer, clients);
  return client?.email || "";
}

function normalizePhoneForWhatsApp(phone, company = {}) {
  let raw = String(phone || "").trim();
  if (!raw) return "";
  raw = raw.replace(/[()\s-]/g, "");
  if (raw.startsWith("00")) raw = `+${raw.slice(2)}`;
  const digitsOnly = raw.replace(/[^\d+]/g, "");
  if (digitsOnly.startsWith("+")) return digitsOnly.replace(/^\+/, "");
  if (digitsOnly.startsWith("383") || digitsOnly.startsWith("49") || digitsOnly.startsWith("41") || digitsOnly.startsWith("43")) return digitsOnly;

  const country = String(company?.country || "").toLowerCase();
  if (digitsOnly.startsWith("0")) {
    if (country.includes("kos") || country.includes("kosovo")) return `383${digitsOnly.slice(1)}`;
    if (country.includes("germany") || country.includes("deutschland")) return `49${digitsOnly.slice(1)}`;
    if (country.includes("switzerland") || country.includes("schweiz")) return `41${digitsOnly.slice(1)}`;
    if (country.includes("austria") || country.includes("österreich") || country.includes("osterreich")) return `43${digitsOnly.slice(1)}`;
  }

  return digitsOnly.replace(/^\+/, "");
}

function buildOfferShareText(offer, text, company = {}) {
  const lines = [
    `${text.offerNumber}: ${offer?.offerNumber || "—"}`,
    `${text.titleField}: ${offer?.title || "—"}`,
    `${text.clientName}: ${offer?.clientName || "—"}`,
    `${text.projectName}: ${offer?.projectName || "—"}`,
    `${text.issueDate}: ${formatDate(offer?.issueDate)}`,
    `${text.validUntil}: ${formatDate(offer?.validUntil)}`,
    `${text.subtotal}: ${formatMoney(offer?.subtotal)}`,
    `${text.taxAmount}: ${formatMoney(offer?.taxAmount)}`,
    `${text.grandTotal}: ${formatMoney(offer?.total)}`,
  ];
  if (offer?.address) lines.push(`${text.address}: ${offer.address}`);
  if (offer?.notes) lines.push(`${text.notes}: ${offer.notes}`);
  if (offer?.terms) lines.push(`${text.terms}: ${offer.terms}`);
  if (company?.companyName) lines.push(`${company.companyName}${company.phone ? ` | ${company.phone}` : ""}`);
  return lines.join("\n");
}

function printOfferDocument(offer, text, company = {}) {
  if (!offer || typeof window === "undefined") return;
  const printWindow = window.open("", "_blank", "width=1100,height=1500");
  if (!printWindow) return;

  const rows = (offer.items || []).map((item, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${safeText(item.description || "—")}</td>
      <td>${safeText(item.quantity)}</td>
      <td>${safeText(item.unit || "")}</td>
      <td>${safeText(formatMoney(item.unitPrice))}</td>
      <td>${safeText(formatMoney(n(item.quantity) * n(item.unitPrice)))}</td>
    </tr>
  `).join("");

  const companyName = company?.companyName || "BauHeld";
  const companyAddress = [company?.address, company?.postalCode, company?.city, company?.country].filter(Boolean).join(", ");
  const companyLine = [companyAddress, company?.phone, company?.email, company?.website].filter(Boolean).join(" · ");
  const clientEmail = getClientEmail(offer, []);
  const clientPhone = getClientPhone(offer, []);
  const accent = "#1d4ed8";
  const logoMarkup = company?.logoUrl
    ? `<img src="${safeText(company.logoUrl)}" alt="Logo" class="logo" />`
    : `<div class="logo-fallback">${safeText((companyName || "B").slice(0, 2).toUpperCase())}</div>`;
  const notesLength = String(offer?.notes || "").length + String(offer?.terms || "").length;
  const singlePage = (offer?.items || []).length <= 6 && notesLength <= 320;

  printWindow.document.open();
  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${safeText(text.printTitle)} - ${safeText(offer.offerNumber || offer.title || "Offer")}</title>
        <style>
          @page { size: A4; margin: 11mm; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; }
          body { font-family: Arial, Helvetica, sans-serif; color: #0f172a; background: #eef2ff; }
          .page { width: 210mm; min-height: 297mm; margin: 0 auto 10px; background: white; padding: 15mm 14mm 14mm; page-break-after: always; position: relative; overflow: hidden; }
          .page:last-child { page-break-after: auto; }
          @media screen { body { padding: 8px 0; } }
          @media print { body { background: white; } .page { margin: 0; box-shadow: none; } }
          .top-band { position: absolute; inset: 0 0 auto 0; height: 9mm; background: linear-gradient(90deg, ${accent}, #0f172a); }
          .header { display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; margin-top: 6mm; }
          .brand { display: flex; gap: 14px; align-items: center; }
          .logo { width: 64px; height: 64px; object-fit: contain; border-radius: 16px; background: white; border: 1px solid #dbeafe; padding: 6px; }
          .logo-fallback { width: 64px; height: 64px; border-radius: 16px; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:24px; color:white; background: linear-gradient(135deg, ${accent}, #0f172a); }
          .doc-kicker { font-size: 11px; text-transform: uppercase; letter-spacing: .22em; color: ${accent}; font-weight: 800; margin: 0 0 6px; }
          .doc-title { font-size: 32px; line-height: 1.05; margin: 0; font-weight: 800; }
          .muted { color: #475569; font-size: 12px; line-height: 1.65; }
          .pill { display:inline-flex; align-items:center; padding: 7px 12px; border-radius: 999px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .12em; background: #eff6ff; color: ${accent}; border:1px solid #bfdbfe; }
          .hero { margin-top: 16px; border: 1px solid #dbeafe; background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%); border-radius: 22px; padding: 16px; display:grid; grid-template-columns: 1.2fr 0.8fr; gap: 16px; }
          .hero h2 { margin: 0 0 10px; font-size: 24px; line-height: 1.2; }
          .box-grid { display:grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 16px; }
          .card { border: 1px solid #e2e8f0; border-radius: 18px; padding: 12px 14px; background: #fff; min-height: 78px; }
          .label { font-size: 10px; text-transform: uppercase; letter-spacing: .16em; color: #64748b; margin-bottom: 6px; font-weight: 800; }
          .value { font-size: 14px; font-weight: 700; line-height: 1.55; white-space: pre-wrap; }
          .section-title { font-size: 17px; font-weight: 800; margin: 22px 0 10px; }
          .summary { display:grid; gap: 10px; }
          .summary-row { display:flex; justify-content:space-between; gap: 12px; padding: 12px 14px; border-radius: 16px; border:1px solid #e2e8f0; background:#f8fafc; font-size: 14px; font-weight:700; }
          .summary-row.total { background: linear-gradient(90deg, #eff6ff, #dbeafe); border-color: #93c5fd; font-size: 16px; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th, td { border: 1px solid #e2e8f0; padding: 10px 9px; text-align: left; font-size: 12px; vertical-align: top; }
          th { background: #eff6ff; color: #1e3a8a; font-size: 10px; text-transform: uppercase; letter-spacing: .14em; }
          tbody tr:nth-child(even) td { background: #f8fafc; }
          .signature-grid { display:grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 18px; }
          .signature-box { border:1px dashed #cbd5e1; border-radius: 18px; padding: 16px; min-height: 90px; }
          .signature-line { margin-top: 34px; border-top: 1px solid #94a3b8; }
          .footer { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #64748b; display:flex; justify-content:space-between; gap: 16px; }
          .page-no { text-align:right; font-weight:800; }
        </style>
      </head>
      <body>
        <section class="page">
          <div class="top-band"></div>
          <div class="header">
            <div class="brand">
              ${logoMarkup}
              <div>
                <div class="doc-kicker">${safeText(text.title)}</div>
                <h1 class="doc-title">${safeText(offer.offerNumber || offer.title || text.title)}</h1>
                <div class="muted">${safeText(companyName)}${companyLine ? `<br/>${safeText(companyLine)}` : ""}</div>
              </div>
            </div>
            <div class="page-no">
              <div class="pill">${safeText(text.page)} 1 / ${singlePage ? 1 : 2}</div>
            </div>
          </div>

          <div class="hero">
            <div>
              <div class="label">${safeText(text.printPreparedFor)}</div>
              <h2>${safeText(offer.clientName || "—")}</h2>
              <div class="muted">${safeText(offer.address || "—")}${clientEmail ? `<br/>${safeText(clientEmail)}` : ""}${clientPhone ? `<br/>${safeText(clientPhone)}` : ""}</div>
            </div>
            <div>
              <div class="summary">
                <div class="summary-row"><span>${safeText(text.subtotal)}</span><span>${safeText(formatMoney(offer.subtotal))}</span></div>
                <div class="summary-row"><span>${safeText(text.taxAmount)} (${safeText(offer.taxRate)}%)</span><span>${safeText(formatMoney(offer.taxAmount))}</span></div>
                <div class="summary-row total"><span>${safeText(text.grandTotal)}</span><span>${safeText(formatMoney(offer.total))}</span></div>
              </div>
            </div>
          </div>

          <div class="box-grid">
            <div class="card"><div class="label">${safeText(text.titleField)}</div><div class="value">${safeText(offer.title || "—")}</div></div>
            <div class="card"><div class="label">${safeText(text.status)}</div><div class="value">${safeText(text[offer.status] || offer.status || "—")}</div></div>
            <div class="card"><div class="label">${safeText(text.projectName)}</div><div class="value">${safeText(offer.projectName || "—")}</div></div>
            <div class="card"><div class="label">${safeText(text.offerNumber)}</div><div class="value">${safeText(offer.offerNumber || "—")}</div></div>
            <div class="card"><div class="label">${safeText(text.issueDate)}</div><div class="value">${safeText(formatDate(offer.issueDate))}</div></div>
            <div class="card"><div class="label">${safeText(text.validUntil)}</div><div class="value">${safeText(formatDate(offer.validUntil))}</div></div>
          </div>

          <div class="section-title">${safeText(text.notesBlock)}</div>
          <div class="box-grid">
            <div class="card"><div class="label">${safeText(text.notes)}</div><div class="value">${safeText(offer.notes || text.thankYou || "—")}</div></div>
            <div class="card"><div class="label">${safeText(text.terms)}</div><div class="value">${safeText(offer.terms || "—")}</div></div>
          </div>

          ${singlePage ? `
          <div class="section-title">${safeText(text.items)}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>${safeText(text.description)}</th>
                <th>${safeText(text.quantity)}</th>
                <th>${safeText(text.unit)}</th>
                <th>${safeText(text.unitPrice)}</th>
                <th>${safeText(text.lineTotal)}</th>
              </tr>
            </thead>
            <tbody>${rows || `<tr><td colspan="6">—</td></tr>`}</tbody>
          </table>
          ` : ""}

          <div class="signature-grid">
            <div class="signature-box">
              <div class="label">${safeText(companyName)}</div>
              <div class="signature-line"></div>
              <div class="muted">${safeText(text.signature)}</div>
            </div>
            <div class="signature-box">
              <div class="label">${safeText(offer.clientName || text.clientName)}</div>
              <div class="signature-line"></div>
              <div class="muted">${safeText(text.signature)}</div>
            </div>
          </div>

          <div class="footer">
            <div>${safeText(companyName)}</div>
            <div>${safeText(text.page)} 1 / ${singlePage ? 1 : 2}</div>
          </div>
        </section>

        ${singlePage ? "" : `
        <section class="page">
          <div class="top-band"></div>
          <div class="header">
            <div>
              <div class="doc-kicker">${safeText(text.items)}</div>
              <h1 class="doc-title">${safeText(offer.title || offer.offerNumber || text.items)}</h1>
              <div class="muted">${safeText(offer.offerNumber || "—")} · ${safeText(offer.clientName || "—")}</div>
            </div>
            <div class="page-no">
              <div class="pill">${safeText(text.page)} 2 / 2</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>${safeText(text.description)}</th>
                <th>${safeText(text.quantity)}</th>
                <th>${safeText(text.unit)}</th>
                <th>${safeText(text.unitPrice)}</th>
                <th>${safeText(text.lineTotal)}</th>
              </tr>
            </thead>
            <tbody>${rows || `<tr><td colspan="6">—</td></tr>`}</tbody>
          </table>

          <div class="section-title">${safeText(text.totals)}</div>
          <div class="summary">
            <div class="summary-row"><span>${safeText(text.subtotal)}</span><span>${safeText(formatMoney(offer.subtotal))}</span></div>
            <div class="summary-row"><span>${safeText(text.taxAmount)} (${safeText(offer.taxRate)}%)</span><span>${safeText(formatMoney(offer.taxAmount))}</span></div>
            <div class="summary-row total"><span>${safeText(text.grandTotal)}</span><span>${safeText(formatMoney(offer.total))}</span></div>
          </div>

          <div class="footer">
            <div>${safeText(companyName)}</div>
            <div>${safeText(text.page)} 2 / 2</div>
          </div>
        </section>
        `}
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.print();
    printWindow.onafterprint = () => printWindow.close();
  };
}

function statusClasses(status, theme) {
  const map = {
    draft: theme === "dark" ? "bg-slate-800 text-slate-200 border-slate-700" : "bg-slate-100 text-slate-700 border-slate-200",
    sent: theme === "dark" ? "bg-blue-500/15 text-blue-300 border-blue-400/20" : "bg-blue-50 text-blue-700 border-blue-200",
    approved: theme === "dark" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/20" : "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: theme === "dark" ? "bg-rose-500/15 text-rose-300 border-rose-400/20" : "bg-rose-50 text-rose-700 border-rose-200",
  };
  return map[status] || map.draft;
}

function OfferModal({
  open,
  onClose,
  onSave,
  form,
  setForm,
  projects,
  clients,
  editingId,
  text,
  theme,
}) {
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const shellClass =
    theme === "dark"
      ? "border-slate-800 bg-slate-950 text-slate-100"
      : "border-slate-200 bg-white text-slate-900";

  const totals = computeTotals(form.items, form.taxRate);

  const updateItem = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [key]: value } : item
      ),
    }));
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { ...emptyItem, id: `item-${Date.now()}` }],
    }));
  };

  const removeItem = (id) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((item) => item.id !== id) : prev.items,
    }));
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-slate-950/45 px-4 py-6 backdrop-blur-[3px] md:px-6 md:py-10">
      <div className="mx-auto flex min-h-full items-start justify-center md:items-center">
        <div className={`relative flex max-h-[calc(100vh-3rem)] w-full max-w-6xl flex-col overflow-hidden rounded-[30px] border shadow-[0_30px_80px_rgba(15,23,42,0.18)] md:max-h-[calc(100vh-5rem)] ${shellClass}`}>
          <div className={`sticky top-0 z-10 border-b px-4 py-4 md:px-6 ${theme === "dark" ? "border-slate-800 bg-slate-950/95" : "border-slate-200 bg-white/95"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-xl font-black md:text-2xl">{editingId ? text.editOffer : text.newOffer}</h3>
                <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition ${theme === "dark" ? "border-slate-800 bg-slate-900 hover:bg-slate-800" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <form onSubmit={onSave} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <UILabel>{text.offerNumber}</UILabel>
                <UIInput value={form.offerNumber} onChange={(e) => setForm((prev) => ({ ...prev, offerNumber: e.target.value }))} placeholder={text.generatedLabel} />
              </div>
              <div className="md:col-span-2 xl:col-span-2">
                <UILabel>{text.titleField}</UILabel>
                <UIInput value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} placeholder={text.titleField} required />
              </div>
              <div>
                <UILabel>{text.status}</UILabel>
                <select
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>{text[status]}</option>
                  ))}
                </select>
              </div>

              <div>
                <UILabel>{text.projectLink}</UILabel>
                <select
                  value={form.projectId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedProject = projects.find((project) => project.id === selectedId);
                    setForm((prev) => ({
                      ...prev,
                      projectId: selectedId,
                      projectName: selectedProject?.name || prev.projectName || "",
                      clientId: prev.clientId || selectedProject?.clientId || "",
                      clientName: prev.clientName || selectedProject?.clientName || "",
                      clientEmail: prev.clientEmail || selectedProject?.clientEmail || "",
                      clientPhone: prev.clientPhone || selectedProject?.clientPhone || "",
                      address: prev.address || selectedProject?.address || "",
                    }));
                  }}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  <option value="">{text.noProjectLinked}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name || "Project"}</option>
                  ))}
                </select>
              </div>

              <div>
                <UILabel>{text.projectName}</UILabel>
                <UIInput value={form.projectName} onChange={(e) => setForm((prev) => ({ ...prev, projectName: e.target.value }))} placeholder={text.projectName} />
              </div>

              <div>
                <UILabel>{text.clientLink}</UILabel>
                <select
                  value={form.clientId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedClient = clients.find((client) => client.id === selectedId);
                    setForm((prev) => ({
                      ...prev,
                      clientId: selectedId,
                      clientName: selectedClient?.name || selectedClient?.companyName || selectedClient?.contactPerson || prev.clientName || "",
                      clientEmail: selectedClient?.email || prev.clientEmail || "",
                      clientPhone: selectedClient?.phone || selectedClient?.mobile || prev.clientPhone || "",
                    }));
                  }}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  <option value="">{text.noClientLinked}</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name || client.companyName || client.contactPerson || "Client"}</option>
                  ))}
                </select>
              </div>

              <div>
                <UILabel>{text.clientName}</UILabel>
                <UIInput value={form.clientName} onChange={(e) => setForm((prev) => ({ ...prev, clientName: e.target.value }))} placeholder={text.clientName} />
              </div>

              <div>
                <UILabel>{text.clientEmail}</UILabel>
                <UIInput value={form.clientEmail} onChange={(e) => setForm((prev) => ({ ...prev, clientEmail: e.target.value }))} placeholder={text.clientEmail} />
              </div>

              <div>
                <UILabel>{text.clientPhone}</UILabel>
                <UIInput value={form.clientPhone} onChange={(e) => setForm((prev) => ({ ...prev, clientPhone: e.target.value }))} placeholder="+383..." />
              </div>

              <div className="md:col-span-2 xl:col-span-2">
                <UILabel>{text.address}</UILabel>
                <UIInput value={form.address} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))} placeholder={text.address} />
              </div>

              <div>
                <UILabel>{text.issueDate}</UILabel>
                <UIInput type="date" value={form.issueDate} onChange={(e) => setForm((prev) => ({ ...prev, issueDate: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.validUntil}</UILabel>
                <UIInput type="date" value={form.validUntil} onChange={(e) => setForm((prev) => ({ ...prev, validUntil: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.taxRate}</UILabel>
                <UIInput type="number" step="0.01" value={form.taxRate} onChange={(e) => setForm((prev) => ({ ...prev, taxRate: e.target.value }))} />
              </div>
            </div>

            <div className={`mt-6 rounded-[28px] border p-4 md:p-5 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-slate-50/70"}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-lg font-black">{text.lineItems}</h4>
                  <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.statusesReady}</p>
                </div>
                <UIButton type="button" onClick={addItem} className="gap-2">
                  <Plus className="h-4 w-4" />
                  {text.addItem}
                </UIButton>
              </div>

              <div className="mt-4 space-y-3">
                {form.items.map((item, index) => {
                  const itemTotal = n(item.quantity) * n(item.unitPrice);
                  return (
                    <div
                      key={item.id}
                      className={`grid gap-3 rounded-[24px] border p-3 md:grid-cols-[1.8fr_0.7fr_0.7fr_0.9fr_auto] ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white"}`}
                    >
                      <div>
                        <UILabel>{text.description}</UILabel>
                        <UIInput value={item.description} onChange={(e) => updateItem(item.id, "description", e.target.value)} placeholder={`${text.description} ${index + 1}`} />
                      </div>
                      <div>
                        <UILabel>{text.quantity}</UILabel>
                        <UIInput type="number" step="0.01" value={item.quantity} onChange={(e) => updateItem(item.id, "quantity", e.target.value)} />
                      </div>
                      <div>
                        <UILabel>{text.unit}</UILabel>
                        <UIInput value={item.unit} onChange={(e) => updateItem(item.id, "unit", e.target.value)} />
                      </div>
                      <div>
                        <UILabel>{text.unitPrice}</UILabel>
                        <UIInput type="number" step="0.01" value={item.unitPrice} onChange={(e) => updateItem(item.id, "unitPrice", e.target.value)} />
                      </div>
                      <div className="flex flex-col justify-end gap-2">
                        <div className={`rounded-2xl border px-3 py-3 text-sm font-black text-center ${theme === "dark" ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}>
                          {formatMoney(itemTotal)}
                        </div>
                        <UIButton type="button" variant="secondary" onClick={() => removeItem(item.id)} className="h-12 w-12 p-0">
                          <Trash2 className="h-4 w-4" />
                        </UIButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="space-y-4">
                <div>
                  <UILabel>{text.notes}</UILabel>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder={text.notesPlaceholder}
                    rows={4}
                    className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500" : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400"}`}
                  />
                </div>
                <div>
                  <UILabel>{text.terms}</UILabel>
                  <textarea
                    value={form.terms}
                    onChange={(e) => setForm((prev) => ({ ...prev, terms: e.target.value }))}
                    placeholder={text.termsPlaceholder}
                    rows={4}
                    className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500" : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400"}`}
                  />
                </div>
              </div>

              <div className={`rounded-[28px] border p-4 md:p-5 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-slate-50/70"}`}>
                <h4 className="text-lg font-black">{text.totals}</h4>
                <div className="mt-4 space-y-3">
                  <PriceRow label={text.subtotal} value={formatMoney(totals.subtotal)} theme={theme} />
                  <PriceRow label={`${text.taxAmount} (${n(form.taxRate)}%)`} value={formatMoney(totals.taxAmount)} theme={theme} />
                  <PriceRow label={text.grandTotal} value={formatMoney(totals.total)} theme={theme} strong />
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:justify-end">
              <UIButton type="button" variant="secondary" onClick={onClose}>{text.cancel}</UIButton>
              <UIButton type="submit">{editingId ? text.update : text.save}</UIButton>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Offers({ lang = "sq", t = {} }) {
  const text = ui[lang] || ui.sq;
  const { theme, currentTheme } = usePanelTheme();
  const [company, setCompany] = useState(null);

  const [offers, setOffers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const loadOffers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "offers"), orderBy("createdAt", "desc")));
      const rows = snap.docs.map(normalizeOffer);
      setOffers(rows);
      if (!selectedOfferId && rows[0]?.id) setSelectedOfferId(rows[0].id);
    } catch (error) {
      console.error(error);
      notify.error(text.loadError);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const snap = await getDocs(query(collection(db, "projects"), orderBy("createdAt", "desc")));
      setProjects(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.warn("Projects load skipped:", error);
      setProjects([]);
    }
  };

  const loadClients = async () => {
    try {
      const snap = await getDocs(query(collection(db, "invoice_clients"), orderBy("createdAt", "desc")));
      setClients(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.warn("Clients load skipped:", error);
      setClients([]);
    }
  };


  const loadCompany = async () => {
    try {
      const settings = await getCompanySettings();
      setCompany(settings || null);
    } catch (error) {
      console.warn("Company settings load skipped:", error);
      setCompany(null);
    }
  };

  useEffect(() => {
    loadOffers();
    loadProjects();
    loadClients();
    loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOffers = useMemo(() => {
    const needle = String(search || "").toLowerCase().trim();
    return offers.filter((offer) => {
      const matchesStatus = statusFilter === "all" ? true : offer.status === statusFilter;
      const haystack = [offer.offerNumber, offer.title, offer.projectName, offer.clientName].join(" ").toLowerCase();
      const matchesSearch = !needle || haystack.includes(needle);
      return matchesStatus && matchesSearch;
    });
  }, [offers, search, statusFilter]);

  const stats = useMemo(() => {
    const totalOffers = offers.length;
    const approvedOffers = offers.filter((item) => item.status === "approved").length;
    const pendingValue = offers
      .filter((item) => ["draft", "sent"].includes(item.status))
      .reduce((sum, item) => sum + n(item.total), 0);
    const approvedValue = offers
      .filter((item) => item.status === "approved")
      .reduce((sum, item) => sum + n(item.total), 0);
    return { totalOffers, approvedOffers, pendingValue, approvedValue };
  }, [offers]);

  const selectedOffer = useMemo(
    () => filteredOffers.find((item) => item.id === selectedOfferId) || offers.find((item) => item.id === selectedOfferId) || filteredOffers[0] || null,
    [filteredOffers, offers, selectedOfferId]
  );
  const selectedOfferEmail = selectedOffer ? getClientEmail(selectedOffer, clients) : "";
  const selectedOfferPhone = selectedOffer ? getClientPhone(selectedOffer, clients) : "";

  useEffect(() => {
    if (selectedOffer?.id && selectedOffer.id !== selectedOfferId) {
      setSelectedOfferId(selectedOffer.id);
    }
  }, [selectedOffer, selectedOfferId]);

  const openCreate = () => {
    setEditingOffer(null);
    setForm({
      ...initialForm,
      offerNumber: nextOfferNumber(offers),
      issueDate: getToday(),
      validUntil: getValidUntil(),
      items: [{ ...emptyItem, id: `item-${Date.now()}` }],
    });
    setModalOpen(true);
  };

  const openEdit = (offer) => {
    setEditingOffer(offer);
    setForm({
      offerNumber: offer.offerNumber || nextOfferNumber(offers),
      title: offer.title || "",
      projectId: offer.projectId || "",
      projectName: offer.projectName || "",
      clientId: offer.clientId || "",
      clientName: offer.clientName || "",
      clientEmail: offer.clientEmail || getClientEmail(offer, clients) || "",
      clientPhone: offer.clientPhone || getClientPhone(offer, clients) || "",
      address: offer.address || "",
      status: offer.status || "draft",
      issueDate: offer.issueDate || "",
      validUntil: offer.validUntil || "",
      taxRate: offer.taxRate ?? 19,
      notes: offer.notes || "",
      terms: offer.terms || "",
      items: Array.isArray(offer.items) && offer.items.length ? offer.items.map(ensureItem) : [{ ...emptyItem, id: `item-${Date.now()}` }],
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!String(form.title || "").trim()) {
      notify.error(text.requiredTitle);
      return;
    }
    const validItems = (form.items || []).filter((item) => String(item.description || "").trim());
    if (!validItems.length) {
      notify.error(text.requiredItem);
      return;
    }

    try {
      const payload = buildPayload({
        ...form,
        offerNumber: form.offerNumber || nextOfferNumber(offers),
      });
      if (editingOffer?.id) {
        await updateDoc(doc(db, "offers", editingOffer.id), payload);
        notify.success(text.updated);
      } else {
        await addDoc(collection(db, "offers"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        notify.success(text.saved);
      }
      setModalOpen(false);
      setEditingOffer(null);
      setForm(initialForm);
      await loadOffers();
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    }
  };


  const handlePrint = (offer) => {
    printOfferDocument(offer, text, company || {});
  };

  const handleCopyShareText = async (offer) => {
    try {
      await navigator.clipboard.writeText(buildOfferShareText(offer, text, company || {}));
      notify.success(text.copied);
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    }
  };

  const handleSendGmail = (offer) => {
    const email = getClientEmail(offer, clients);
    if (!email) {
      notify.error(text.gmailMissing);
      return;
    }
    const subject = encodeURIComponent(`${text.offerNumber} ${offer.offerNumber || ""} - ${offer.title || ""}`.trim());
    const body = encodeURIComponent(buildOfferShareText(offer, text, company || {}));
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${subject}&body=${body}`, "_blank", "noopener,noreferrer");
  };

  const handleSendWhatsApp = (offer) => {
    const phone = normalizePhoneForWhatsApp(getClientPhone(offer, clients), company || {});
    if (!phone) {
      notify.error(text.whatsappMissing);
      return;
    }
    const message = encodeURIComponent(buildOfferShareText(offer, text, company || {}));
    window.open(`https://wa.me/${phone}?text=${message}`, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(text.confirmDelete)) return;
    try {
      await deleteDoc(doc(db, "offers", offer.id));
      notify.success(text.deleted);
      if (selectedOfferId === offer.id) setSelectedOfferId("");
      await loadOffers();
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    }
  };

  return (
    <PageShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-black tracking-tight md:text-3xl">{text.title}</h2>
            <p className={`mt-1 text-sm ${currentTheme.muted}`}>{text.subtitle}</p>
          </div>
          <UIButton onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            {text.newOffer}
          </UIButton>
        </div>

        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <StatCard title={text.totalOffers} value={String(stats.totalOffers)} subtitle={offers.length ? "" : text.noOffersText} icon={FileBadge2} theme={theme} />
          <StatCard title={text.approvedOffers} value={String(stats.approvedOffers)} subtitle={text.approved} icon={ShieldCheck} theme={theme} />
          <StatCard title={text.pendingValue} value={formatMoney(stats.pendingValue)} subtitle={`${text.draft} / ${text.sent}`} icon={CircleDashed} theme={theme} />
          <StatCard title={text.approvedValue} value={formatMoney(stats.approvedValue)} subtitle={text.approved} icon={Euro} theme={theme} accent />
        </div>

        <PanelCard>
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="relative flex-1">
              <Search className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`} />
              <UIInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder={text.searchPlaceholder} className="pl-11" />
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[200px]">
                <Filter className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  <option value="all">{text.allStatuses}</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>{text[status]}</option>
                  ))}
                </select>
              </div>
              <UIButton onClick={openCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                {text.newOffer}
              </UIButton>
            </div>
          </div>
        </PanelCard>

        <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
          <PanelCard>
            <SectionHeading title={text.listTitle} subtitle={`${filteredOffers.length} ${text.listSubtitle}`} />

            <div className="mt-4 space-y-4">
              {loading ? (
                <div className={`rounded-[26px] border border-dashed px-6 py-12 text-center text-sm ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                  {t.loading || "Loading..."}
                </div>
              ) : filteredOffers.length ? (
                filteredOffers.map((offer) => {
                  const selected = selectedOfferId === offer.id;
                  return (
                    <button
                      key={offer.id}
                      type="button"
                      onClick={() => setSelectedOfferId(offer.id)}
                      className={`w-full rounded-[28px] border p-4 text-left transition md:p-5 ${selected ? theme === "dark" ? "border-blue-400/30 bg-blue-500/10 shadow-[0_0_0_1px_rgba(96,165,250,0.18)]" : "border-blue-200 bg-blue-50/70 shadow-[0_0_0_1px_rgba(96,165,250,0.08)]" : theme === "dark" ? "border-slate-800 bg-slate-900/60 hover:border-slate-700" : "border-slate-200 bg-white hover:border-slate-300"}`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${statusClasses(offer.status, theme)}`}>
                              {text[offer.status]}
                            </span>
                            <span className={`text-xs font-semibold ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                              {offer.offerNumber || text.generatedLabel}
                            </span>
                            <span className={`text-xs font-semibold ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>
                              {text.createdAt}: {formatDate(offer.createdAt)}
                            </span>
                          </div>
                          <div>
                            <h3 className="truncate text-lg font-black md:text-xl">{offer.title || "—"}</h3>
                            <div className={`mt-1 flex flex-wrap items-center gap-3 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                              <span className="inline-flex items-center gap-1.5"><UserRound className="h-4 w-4" />{offer.clientName || "—"}</span>
                              <span className="inline-flex items-center gap-1.5"><FileBadge2 className="h-4 w-4" />{offer.projectName || "—"}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex shrink-0 gap-2 self-start">
                          <UIButton type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); handlePrint(offer); }} className="h-11 w-11 p-0">
                            <Printer className="h-4 w-4" />
                          </UIButton>
                          <UIButton type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); openEdit(offer); }} className="h-11 w-11 p-0">
                            <Pencil className="h-4 w-4" />
                          </UIButton>
                          <UIButton type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); handleDelete(offer); }} className="h-11 w-11 p-0">
                            <Trash2 className="h-4 w-4" />
                          </UIButton>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <MiniMetric label={text.subtotal} value={formatMoney(offer.subtotal)} theme={theme} />
                        <MiniMetric label={text.taxAmount} value={formatMoney(offer.taxAmount)} theme={theme} />
                        <MiniMetric label={text.grandTotal} value={formatMoney(offer.total)} theme={theme} positive={offer.status === "approved"} />
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className={`rounded-[26px] border border-dashed px-6 py-12 text-center ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                  <div className="text-base font-semibold">{offers.length ? text.noResults : text.noOffers}</div>
                  <div className="mt-2 text-sm">{offers.length ? text.noResultsText : text.noOffersText}</div>
                </div>
              )}
            </div>
          </PanelCard>

          <PanelCard>
            {selectedOffer ? (
              <>
                <SectionHeading title={text.details} subtitle={selectedOffer.title} />

                <div className="mt-4 flex flex-wrap gap-2">
                  <UIButton type="button" onClick={() => handlePrint(selectedOffer)} className="gap-2">
                    <Printer className="h-4 w-4" />
                    {text.printOffer}
                  </UIButton>
                  <UIButton type="button" variant="secondary" onClick={() => handleSendGmail(selectedOffer)} className="gap-2">
                    <Mail className="h-4 w-4" />
                    {text.sendGmail}
                  </UIButton>
                  <UIButton type="button" variant="secondary" onClick={() => handleSendWhatsApp(selectedOffer)} className="gap-2">
                    <MessageCircle className="h-4 w-4" />
                    {text.sendWhatsApp}
                  </UIButton>
                  <UIButton type="button" variant="secondary" onClick={() => handleCopyShareText(selectedOffer)} className="gap-2">
                    <Copy className="h-4 w-4" />
                    {text.copyText}
                  </UIButton>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {detailTabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`rounded-2xl px-3 py-2 text-sm font-semibold transition ${activeTab === tab ? theme === "dark" ? "bg-blue-500/15 text-blue-300" : "bg-blue-50 text-blue-700" : theme === "dark" ? "bg-slate-900 text-slate-300 hover:bg-slate-800" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    >
                      {text[tab]}
                    </button>
                  ))}
                </div>

                <div className={`mt-4 rounded-[28px] border p-4 md:p-5 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-slate-50/70"}`}>
                  {activeTab === "overview" ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${statusClasses(selectedOffer.status, theme)}`}>{text[selectedOffer.status]}</span>
                        <span className={`inline-flex items-center gap-2 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}><CheckCircle2 className="h-4 w-4" />{selectedOffer.offerNumber || "—"}</span>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <DetailRow label={text.issueDate} value={formatDate(selectedOffer.issueDate)} />
                        <DetailRow label={text.validUntil} value={formatDate(selectedOffer.validUntil)} />
                        <DetailRow label={text.projectName} value={selectedOffer.projectName || "—"} />
                        <DetailRow label={text.clientName} value={selectedOffer.clientName || "—"} />
                        <DetailRow label={text.address} value={selectedOffer.address || "—"} />
                        <DetailRow label={text.taxRate} value={`${selectedOffer.taxRate}%`} />
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3">
                        <MiniMetric label={text.subtotal} value={formatMoney(selectedOffer.subtotal)} theme={theme} />
                        <MiniMetric label={text.taxAmount} value={formatMoney(selectedOffer.taxAmount)} theme={theme} />
                        <MiniMetric label={text.grandTotal} value={formatMoney(selectedOffer.total)} theme={theme} positive={selectedOffer.status === "approved"} />
                      </div>
                    </div>
                  ) : null}

                  {activeTab === "items" ? (
                    <div className="space-y-3">
                      {selectedOffer.items.map((item) => (
                        <div key={item.id} className={`rounded-[24px] border p-4 ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white"}`}>
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="text-sm font-bold uppercase tracking-[0.18em] text-slate-400">{text.description}</div>
                              <div className="mt-2 text-base font-semibold">{item.description || "—"}</div>
                              <div className={`mt-2 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{n(item.quantity)} {item.unit} × {formatMoney(item.unitPrice)}</div>
                            </div>
                            <div className="text-lg font-black">{formatMoney(n(item.quantity) * n(item.unitPrice))}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {activeTab === "clientProject" ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <DetailCard title={text.clientName} value={selectedOffer.clientName || "—"} theme={theme} />
                      <DetailCard title={text.projectName} value={selectedOffer.projectName || "—"} theme={theme} />
                      <DetailCard title={text.address} value={selectedOffer.address || "—"} theme={theme} />
                      <DetailCard title={text.status} value={text[selectedOffer.status]} theme={theme} />
                    </div>
                  ) : null}

                  {activeTab === "pricing" ? (
                    <div className="space-y-3">
                      <PriceRow label={text.subtotal} value={formatMoney(selectedOffer.subtotal)} theme={theme} />
                      <PriceRow label={`${text.taxAmount} (${selectedOffer.taxRate}%)`} value={formatMoney(selectedOffer.taxAmount)} theme={theme} />
                      <PriceRow label={text.grandTotal} value={formatMoney(selectedOffer.total)} theme={theme} strong />
                    </div>
                  ) : null}

                  {activeTab === "notesBlock" ? (
                    <div className="space-y-4">
                      <div>
                        <p className={`mb-2 text-sm font-bold ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>{text.notes}</p>
                        <p className={`rounded-3xl border px-4 py-3 text-sm leading-6 ${theme === "dark" ? "border-slate-800 bg-slate-950/70 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>{selectedOffer.notes || "—"}</p>
                      </div>
                      <div>
                        <p className={`mb-2 text-sm font-bold ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>{text.terms}</p>
                        <p className={`rounded-3xl border px-4 py-3 text-sm leading-6 ${theme === "dark" ? "border-slate-800 bg-slate-950/70 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>{selectedOffer.terms || "—"}</p>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className={`mt-4 rounded-[28px] border p-4 md:p-5 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-slate-50/70"}`}>
                  <div className="grid gap-4 xl:grid-cols-[minmax(220px,0.8fr)_minmax(280px,1fr)] 2xl:grid-cols-[minmax(220px,0.75fr)_minmax(300px,0.95fr)_minmax(320px,1fr)] 2xl:items-center">
                    <div className="max-w-[260px]">
                      <h4 className="text-xl font-black leading-tight">{text.shareTools}</h4>
                      <p className={`mt-2 text-sm leading-6 ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.shareSubtitle}</p>
                    </div>
                    <div className={`rounded-[22px] border px-4 py-3 text-sm ${theme === "dark" ? "border-slate-800 bg-slate-950/70 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>
                      <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{text.contactData}</div>
                      <div className="mt-3 space-y-2">
                        <div><span className="font-semibold">{text.clientEmail}:</span> {selectedOfferEmail || "—"}</div>
                        <div><span className="font-semibold">{text.clientPhone}:</span> {selectedOfferPhone || "—"}</div>
                      </div>
                      <p className={`mt-3 text-xs leading-5 ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.gmailHelp}</p>
                      <p className={`mt-1 text-xs leading-5 ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.whatsappHelp}</p>
                      {!selectedOfferEmail ? <p className="mt-2 text-xs font-semibold text-amber-600">{text.missingEmailInline}</p> : null}
                      {!selectedOfferPhone ? <p className="mt-1 text-xs font-semibold text-amber-600">{text.missingPhoneInline}</p> : null}
                    </div>
                    <div className="flex flex-wrap gap-3 2xl:justify-end">
                      <button
                        type="button"
                        onClick={() => handleCopyShareText(selectedOffer)}
                        className={`inline-flex h-11 min-w-[150px] items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-semibold transition ${theme === "dark" ? "border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                      >
                        <Copy className="h-4 w-4" />
                        {text.copyText}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendWhatsApp(selectedOffer)}
                        className="inline-flex h-11 min-w-[150px] items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {text.sendWhatsApp}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendGmail(selectedOffer)}
                        className="inline-flex h-11 min-w-[150px] items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700"
                      >
                        <Mail className="h-4 w-4" />
                        {text.sendGmail}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className={`rounded-[26px] border border-dashed px-6 py-12 text-center ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                <div className="text-base font-semibold">{text.noOffers}</div>
                <div className="mt-2 text-sm">{text.noOffersText}</div>
              </div>
            )}
          </PanelCard>
        </div>
      </div>

      <OfferModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        form={form}
        setForm={setForm}
        projects={projects}
        clients={clients}
        editingId={editingOffer?.id}
        text={text}
        theme={theme}
      />
    </PageShell>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, theme, accent = false }) {
  return (
    <div className={`rounded-[28px] border p-5 shadow-sm ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : accent ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`text-sm font-semibold ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{title}</p>
          <p className="mt-3 text-3xl font-black tracking-tight">{value}</p>
          {subtitle ? <p className={`mt-2 text-sm ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>{subtitle}</p> : null}
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${theme === "dark" ? "bg-slate-900 text-slate-300" : accent ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function MiniMetric({ label, value, theme, positive }) {
  return (
    <div className={`rounded-3xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white"}`}>
      <div className={`text-xs font-bold uppercase tracking-[0.18em] ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>{label}</div>
      <div className={`mt-2 text-lg font-black ${positive === undefined ? "" : positive ? theme === "dark" ? "text-emerald-300" : "text-emerald-700" : theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">{value}</p>
    </div>
  );
}

function DetailCard({ title, value, theme }) {
  return (
    <div className={`rounded-[24px] border p-4 ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white"}`}>
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{title}</div>
      <div className="mt-2 text-sm font-semibold">{value}</div>
    </div>
  );
}

function PriceRow({ label, value, theme, strong = false }) {
  return (
    <div className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white"}`}>
      <span className={`text-sm ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>{label}</span>
      <span className={`${strong ? "text-lg font-black" : "text-sm font-bold"}`}>{value}</span>
    </div>
  );
}
